import { RocketLaunch, Verified } from '@mui/icons-material';
import { Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import Avatar from '../profile/info/Avatar';
import { useEffect, useState } from 'react';
import { useCache } from '../api/cache/Cache';
import { SubscriptionStatus } from '../database/user';
import { useNavigate } from 'react-router-dom';

export interface UserHeaderProps {
    username: string;
    displayName?: string;
    isArtist?: boolean;
    subscriptionStatus: SubscriptionStatus;
    subscriptionOverride?: boolean;
    timestamp: string;
    avatarSize?: number;
    showTime?: boolean;
    isEdited?: boolean;
}

const UserHeader: React.FC<UserHeaderProps> = ({
    username,
    displayName,
    isArtist,
    subscriptionStatus,
    subscriptionOverride,
    timestamp,
    avatarSize,
    showTime = true,
    isEdited,
}) => {
    const theme = useTheme();
    const cache = useCache();
    const navigate = useNavigate();
    const [showFallback, setShowFallback] = useState(false);

    useEffect(() => {
        // If displayName is still not available after 3 seconds, show fallback
        if (!displayName) {
            const timer = setTimeout(() => {
                if (!cache.userSummaries.get(username)) {
                    setShowFallback(true);
                }
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [displayName, username, cache.userSummaries]);

    const getDisplayName = () => {
        if (displayName) return displayName;
        if (showFallback) return '[Deleted User]';
        return 'Loading...';
    };

    const handleProfileClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent parent click handlers from firing
        navigate(`/profile/${username}`);
    };

    return (
        <Stack direction='row' alignItems='center'>
            <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.92 }}
                transition={{ type: 'spring', stiffness: 400, damping: 16 }}
                onClick={handleProfileClick}
                style={{ cursor: 'pointer', flexShrink: 0 }}
            >
                <Avatar
                    username={username}
                    displayName={displayName}
                    size={avatarSize || 50}
                />
            </motion.div>
            <Stack paddingLeft='5px'>
                <Typography
                    variant='body2'
                    fontWeight='bold'
                    marginBottom='0px'
                    onClick={handleProfileClick}
                    sx={{
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {getDisplayName()}
                </Typography>
                <Typography variant='caption'>
                    {new Date(timestamp).toLocaleDateString()}
                    {showTime && (
                        <>
                            {' @ '}
                            {new Date(timestamp).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                            })}
                        </>
                    )}
                    {isEdited && (
                        <Typography
                            component='span'
                            variant='caption'
                            sx={{ opacity: 0.6, ml: 0.5 }}
                        >
                            (Edited)
                        </Typography>
                    )}
                </Typography>
            </Stack>
            {isArtist && (
                <Verified sx={{ marginLeft: 1, color: theme.palette.primary.main }} />
            )}
            {!isArtist &&
                (subscriptionOverride ||
                    subscriptionStatus === SubscriptionStatus.SUBSCRIBED) && (
                    <Tooltip title='Subscribed' placement='top' enterTouchDelay={0}>
                        <RocketLaunch
                            sx={{
                                marginLeft: 1,
                                color: 'gold',
                                fontSize: '1.2rem',
                            }}
                        />
                    </Tooltip>
                )}
        </Stack>
    );
};

export default UserHeader;
